import { toast } from 'react-toastify';
import api from '../services/api';

const MIME_BY_EXT = {
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  txt: 'text/plain',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  xml: 'application/xml'
};

function guessMimeFromFilename(filename) {
  const ext = String(filename || '').split('.').pop()?.toLowerCase();
  return MIME_BY_EXT[ext] || 'application/octet-stream';
}

function getDocumentStoredFilename(doc) {
  return doc?.file_name ?? doc?.document ?? doc?.filename ?? null;
}

function getDocumentDisplayName(doc) {
  return (
    doc?.document_name ??
    doc?.name ??
    getDocumentStoredFilename(doc) ??
    'document'
  );
}

/**
 * Open a saved patient document in a new browser tab via authenticated API fetch.
 * Avoids window.open on /api/uploads/... which can load the React SPA instead of the file.
 */
export async function openPatientDocumentView(practiceId, patientId, doc) {
  if (practiceId == null || patientId == null) {
    toast.error('Missing practice or patient');
    return;
  }
  if (doc?.id == null) {
    toast.error('Save the patient before viewing this document');
    return;
  }

  const storedFilename = getDocumentStoredFilename(doc);
  const displayName = getDocumentDisplayName(doc);

  try {
    const response = await api.get(
      `/practices/${practiceId}/patients/${patientId}/documents/${doc.id}/file`,
      { responseType: 'blob' }
    );

    const contentType =
      response.headers?.['content-type'] ||
      guessMimeFromFilename(storedFilename || displayName);
    const blob = new Blob([response.data], { type: contentType });
    const objectUrl = URL.createObjectURL(blob);

    const opened = window.open(objectUrl, '_blank', 'noopener,noreferrer');
    if (!opened) {
      const link = document.createElement('a');
      link.href = objectUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.click();
    }

    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 120000);
  } catch (err) {
    let message = 'Could not open document';
    const data = err.response?.data;
    if (data instanceof Blob) {
      try {
        const text = await data.text();
        const parsed = JSON.parse(text);
        if (parsed?.message) message = parsed.message;
      } catch {
        /* ignore parse errors */
      }
    } else if (err.response?.data?.message) {
      message = err.response.data.message;
    } else if (err.response?.status === 404) {
      message = 'Document file not found';
    }
    toast.error(message);
  }
}

export function formatPatientDocumentFileLabel(doc) {
  return getDocumentStoredFilename(doc) || '—';
}

export function formatPatientDocumentName(doc) {
  return getDocumentDisplayName(doc);
}
